<script setup>
import SchematisationActionMenu from '@/components/schematisation/SchematisationActionMenu.vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import DikeSchematisationRow from '@/components/schematisation/DikeSchematisationRow.vue'

const props = defineProps({
  items: {},
  hideUnimportantItems: { type: Boolean, default: false }
})

const { x, y, showMenu, show } = actionMenuMixin()

const store = useStore()
const route = useRoute()
const router = useRouter()

const currentOverviewSchematisation = computed(
  () => store.state.stabSchematisation.currentOverviewItem
)
const currentProject = computed(() => store.state.project.currentProject)

const isDeleteSelf = computed(() => store.getters['stabSchematisation/isDeleteSelf'])
const checkIfDeleteSelf = computed(() => isDeleteSelf.value(route))

const setCurrentSchematisation = async (stabSchematisation) => {
  await store.dispatch('stabSchematisation/setCurrentItem', stabSchematisation)
}

const resetChildrenState = async () => await store.dispatch('stabSchematisation/resetChildrenState')

const navigateToSchematisation = async (stabSchematisation) => {
  if (stabSchematisation?.id !== currentOverviewSchematisation.value?.id) {
    resetChildrenState()
  }
  setCurrentSchematisation(stabSchematisation)
  router.push({
    name: 'schematisation-stage',
    params: {
      id: currentProject.value.id,
      revisionId: stabSchematisation.stage_configuration_revision.id,
      schematisationId: stabSchematisation.id,
      schematisationStageId: stabSchematisation.first_not_done_schematisation_stage
    }
  })
}

const toggleDialog = () => store.dispatch('stabSchematisation/toggleDialog')
const toggleDeleteDialog = () => store.dispatch('stabSchematisation/toggleDeleteDialog')
const toggleDuplicateDialog = () => store.dispatch('stabSchematisation/toggleDuplicateDialog')
const toggleExportFactSheetsDialog = () =>
  store.dispatch('stabSchematisation/toggleExportFactSheetsDialog')
const toggleExportDStabilityDialog = () =>
  store.dispatch('stabSchematisation/toggleExportDStabilityDialog')
const toggleExportJsonDialog = () => store.dispatch('stabSchematisation/toggleExportJsonDialog')
const addSchematisation = async () => {
  await setCurrentSchematisation({
    dike_profile: currentOverviewSchematisation.value.dike_profile,
    relates_to: currentOverviewSchematisation.value.id
  })
  toggleDialog()
}

const headers = [
  { title: '', width: '0%', space: true },
  { title: 'Name', width: '15%' },
  { title: 'Section', width: '5%', align: 'center', unimportant: true },
  { title: 'Profile', width: '5%', align: 'center', unimportant: true },
  { title: 'Year' },
  { title: 'Stage configuration', width: '15%' },
  { title: 'Soil type', width: '15%' },
  { title: 'Water line rule', width: '15%' },
  { title: 'Linked' },
  { title: 'Stage', width: '5%', align: 'center' }
]

const filteredHeaders = headers.filter((header) =>
  props.hideUnimportantItems ? !header.unimportant : !header.space
)
</script>

<template>
  <div>
    <v-divider />
    <v-data-table
      :items="items"
      :headers="filteredHeaders"
      hover
      density="compact"
      class="schema-table"
    >
      <template v-slot:headers="{ props }">
        <tr v-bind="props">
          <th class="px-2" v-for="header in filteredHeaders" :key="header.title" v-bind="header">
            <span v-if="header.title !== 'Stage'">{{ header.title }}</span>
            <v-tooltip left v-else width="300px">
              <template v-slot:activator="{ props }">
                <div v-bind="props">
                  <span style="font-size: 12px">Stage</span>
                </div>
              </template>
              <span
                >These stages are marked as done inside the schematisation, the done stages will be
                displayed in the overview and can be used for creating linked schematisations.
              </span>
            </v-tooltip>
          </th>
        </tr>
      </template>
      <template v-slot:item="{ item }">
        <DikeSchematisationRow
          :key="item.id"
          :item="item"
          :hideUnimportantItems="hideUnimportantItems"
          @click-row="navigateToSchematisation(item)"
          @show="show"
        />
      </template>
    </v-data-table>
    <v-divider thickness="2" />
    <SchematisationActionMenu
      @update:show-menu="showMenu = $event"
      :show-menu="showMenu"
      :delete-disabled="checkIfDeleteSelf"
      :x="x"
      :y="y"
      :navigateButton="false"
      @update="showMenu = false"
      @add="addSchematisation"
      @delete="toggleDeleteDialog"
      @duplicate="toggleDuplicateDialog"
      @export="toggleExportFactSheetsDialog"
      @exportDStab="toggleExportDStabilityDialog"
      @exportJson="toggleExportJsonDialog"
    ></SchematisationActionMenu>
  </div>
</template>
<style scoped>
:deep(.v-table__wrapper) th span {
  font-size: 12px;
}

.schema-table :deep(.v-data-table-footer) {
  font-size: 12px;
}
.schema-table :deep(.v-data-table-footer .v-field) {
  font-size: 12px;
}
</style>
